import { getApiBase } from "@app/services/apiConfig";
import type { PermissionProfile } from "@app/services/aiSupervisorApi";

const API_BASE = getApiBase();
const AI_BASE = `${API_BASE}/api/ai`;

export type AiAssistantConversation = {
  id: string;
  title: string;
  visibility: string;
  createdAt: string;
  lastMessageAt: string | null;
  pinned: boolean;
  summary?: string | null;
};

export type AiAssistantMessage = {
  id: string;
  conversationId: string;
  role: "user" | "assistant" | "system";
  content: string;
  createdAt: string;
  tokensEstimated?: number | null;
  attachments: unknown[];
  memories: unknown[];
};

export type AiAssistantTemplate = {
  id: string;
  label: string;
  content: string;
  category?: string | null;
};

export type AiAssistantSummary = {
  totalConversations: number;
  activeConversations: number;
  totalTokens24h: number;
  lastInteractionAt?: string | null;
  warnings: string[];
};

export type AiAssistantPerformanceSnapshot = {
  generatedAt: string;
  periodHours: number;
  knowledgeVersion: {
    versionId: string;
    generatedAt: string;
    fileCount: number;
    categories: Record<string, number>;
  };
  decisionSummary: Record<string, unknown>;
  tradeSummary: Record<string, unknown>;
  metrics: Array<{
    id: string;
    label: string;
    value: number;
    unit?: string | null;
    notes?: string | null;
  }>;
  notes: string[];
};

export type AiAssistantDashboard = {
  conversations: AiAssistantConversation[];
  messages: AiAssistantMessage[];
  templates: AiAssistantTemplate[];
  summary: AiAssistantSummary;
  lastRefreshedAt: string;
  permissionProfile: PermissionProfile;
  performanceSnapshot?: AiAssistantPerformanceSnapshot | null;
};

export type AiAssistantInsights = {
  recommendations?: Record<string, unknown>;
  recentDecisions?: unknown[];
  contextSnapshot?: Record<string, unknown>;
};

export type AiAssistantSendResponse = {
  conversation: AiAssistantConversation;
  messages: AiAssistantMessage[];
  assistantMessage: AiAssistantMessage;
  insights: AiAssistantInsights;
  generatedAt: string;
  permissionDecision?: Record<string, unknown>;
  permissionProfile: PermissionProfile;
};

export type AiAssistantMessagePayload = {
  conversationId: string | null;
  message: string;
  roleId?: string;
  planId?: string;
};

function mapConversation(raw: any): AiAssistantConversation {
  return {
    id: raw.id,
    title: raw.title,
    visibility: raw.visibility,
    createdAt: raw.created_at,
    lastMessageAt: raw.last_message_at ?? null,
    pinned: Boolean(raw.pinned),
    summary: raw.summary ?? null,
  };
}

function mapMessage(raw: any): AiAssistantMessage {
  return {
    id: raw.id,
    conversationId: raw.conversation_id,
    role: raw.role,
    content: raw.content,
    createdAt: raw.created_at,
    tokensEstimated: raw.tokens_estimated ?? null,
    attachments: raw.attachments ?? [],
    memories: raw.memories ?? [],
  };
}

function mapTemplate(raw: any): AiAssistantTemplate {
  return {
    id: raw.id,
    label: raw.label,
    content: raw.content,
    category: raw.category ?? null,
  };
}

function mapSummary(raw: any): AiAssistantSummary {
  return {
    totalConversations: raw.total_conversations ?? 0,
    activeConversations: raw.active_conversations ?? 0,
    totalTokens24h: raw.total_tokens_24h ?? 0,
    lastInteractionAt: raw.last_interaction_at ?? null,
    warnings: raw.warnings ?? [],
  };
}

async function handleResponse<T>(response: Response): Promise<T> {
  if (!response.ok) {
    let message = `Request failed with status ${response.status}`;
    try {
      const payload = await response.json();
      if (typeof payload?.detail === "string") {
        message = payload.detail;
      }
    } catch (error) {
      // ignore parsing errors
    }
    throw new Error(message);
  }
  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

export type FetchAssistantParams = {
  conversationId?: string | null;
  roleId?: string;
  planId?: string;
};

export async function fetchAssistantDashboard({
  conversationId,
  roleId,
  planId,
}: FetchAssistantParams = {}): Promise<AiAssistantDashboard> {
  const params = new URLSearchParams();
  if (conversationId) {
    params.append("conversation_id", conversationId);
  }
  if (roleId) {
    params.append("role_id", roleId);
  }
  if (planId) {
    params.append("plan_id", planId);
  }
  const query = params.toString();
  const response = await fetch(`${AI_BASE}/assistant${query ? `?${query}` : ""}`);
  const payload = await handleResponse<any>(response);

  return {
    conversations: Array.isArray(payload.conversations) ? payload.conversations.map(mapConversation) : [],
    messages: Array.isArray(payload.messages) ? payload.messages.map(mapMessage) : [],
    templates: Array.isArray(payload.templates) ? payload.templates.map(mapTemplate) : [],
    summary: mapSummary(payload.summary ?? {}),
    lastRefreshedAt: payload.last_refreshed_at ?? payload.lastRefreshedAt ?? "",
    permissionProfile: payload.permissionProfile,
    performanceSnapshot: payload.performanceSnapshot ?? null,
  };
}

export async function sendAssistantMessage(payload: AiAssistantMessagePayload): Promise<AiAssistantSendResponse> {
  const response = await fetch(`${AI_BASE}/assistant`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      conversationId: payload.conversationId,
      message: payload.message,
      roleId: payload.roleId,
      planId: payload.planId,
    }),
  });

  const raw = await handleResponse<any>(response);

  return {
    conversation: mapConversation(raw.conversation),
    messages: Array.isArray(raw.messages) ? raw.messages.map(mapMessage) : [],
    assistantMessage: mapMessage(raw.assistantMessage),
    insights: raw.insights ?? {},
    generatedAt: raw.generated_at ?? raw.generatedAt ?? "",
    permissionDecision: raw.permissionDecision,
    permissionProfile: raw.permissionProfile,
  };
}
